import { Checkbox } from "@legal-500/legal500-design-system";
import { isBarRegion, type PickerRegion } from "@/api/mailing-groups.service";
import { regionKey } from "./useRegionPicker";
import styles from "./MailingGroups.module.css";

type UsedRegionLabelMode = "groupName" | "inAGroup";

type RegionPickerSectionProps = {
  regions: PickerRegion[];
  selectedRegionKeys: Set<string>;
  usedRegionsById: ReadonlyMap<number | string, { mailingGroupName: string | null }>;
  isRegionUsed: (region: PickerRegion) => boolean;
  isRegionSelectable: (region: PickerRegion) => boolean;
  isBar?: (region: Pick<PickerRegion, "type">) => boolean;
  onToggle: (region: PickerRegion) => void;
  onSelectAll: () => void;
  onClear: () => void;
  /** Whether the action buttons (Select all / Clear) are shown. */
  showActions: boolean;
  /** Replaces the selection with all selectable non-bar regions. Omit to hide the button. */
  onSelectNonBar?: () => void;
  /** Replaces the selection with all selectable bar regions. Omit to hide the button. */
  onSelectBar?: () => void;
  countriesLoading: boolean;
  countriesError: unknown;
  /** Shown before regions can load (e.g. no publication chosen yet). */
  disabledHint?: string;
  /** "groupName" renders "in <group>"; "inAGroup" renders "IN A GROUP". */
  usedRegionLabelMode: UsedRegionLabelMode;
  error?: string;
};

export function RegionPickerSection({
  regions,
  selectedRegionKeys,
  usedRegionsById,
  isRegionUsed,
  isRegionSelectable,
  isBar = isBarRegion,
  onToggle,
  onSelectAll,
  onClear,
  showActions,
  onSelectNonBar,
  onSelectBar,
  countriesLoading,
  countriesError,
  disabledHint,
  usedRegionLabelMode,
  error,
}: RegionPickerSectionProps) {
  const hasNonBarRegions = regions.some(r => !isBar(r));
  const hasBarRegions = regions.some(isBar);

  return (
    <div className={styles.regionSection}>
      <div className={styles.regionHeader}>
        <span className={styles.sectionLabel}>Regions</span>
        {showActions && (
          <div className={styles.regionActions}>
            <button type="button" className={styles.regionAction} onClick={onSelectAll}>
              Select all
            </button>
            {onSelectNonBar && hasNonBarRegions && (
              <button type="button" className={styles.regionAction} onClick={onSelectNonBar}>
                Select non-bar
              </button>
            )}
            {onSelectBar && hasBarRegions && (
              <button type="button" className={styles.regionAction} onClick={onSelectBar}>
                Select bar
              </button>
            )}
            <button type="button" className={styles.regionAction} onClick={onClear}>
              Clear
            </button>
          </div>
        )}
      </div>
      {disabledHint ? (
        <p className={styles.regionHint}>{disabledHint}</p>
      ) : countriesLoading ? (
        <p className={styles.regionHint}>Loading regions…</p>
      ) : countriesError ? (
        <p className={styles.regionError}>Failed to load regions. Please try again.</p>
      ) : regions.length === 0 ? (
        <p className={styles.regionHint}>No regions found for this publication.</p>
      ) : (
        <div className={styles.regionGrid}>
          {regions.map(region => {
            const key = regionKey(region);
            const bar = isBar(region);
            const used = isRegionUsed(region);
            const disabled = !isRegionSelectable(region);
            const usedBy = region.id !== null ? usedRegionsById.get(region.id) : undefined;
            return (
              <label
                key={key}
                className={`${styles.regionItem} ${disabled ? styles.regionItemDisabled : ""}`}
                title={usedBy ? `Already used in ${usedBy.mailingGroupName}` : undefined}
              >
                <Checkbox
                  checked={selectedRegionKeys.has(key) && !used}
                  disabled={disabled}
                  onChange={() => onToggle(region)}
                  aria-label={region.name ?? `Region ${region.id}`}
                />
                <span className={styles.regionName}>{region.name ?? `Region ${region.id}`}</span>
                {bar && <span className={styles.barBadge}>BAR</span>}
                {usedBy && (
                  <span className={styles.regionUsedHint}>
                    {usedRegionLabelMode === "inAGroup"
                      ? "IN A GROUP"
                      : `in ${usedBy.mailingGroupName}`}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}
      {error && <p className={styles.fieldError}>{error}</p>}
    </div>
  );
}
